import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { hrefForUserProfile } from '@/utils/username';
import _tabHome from '@/assets/mobile-tab-bar/home_icon.png';
import _tabSearch from '@/assets/mobile-tab-bar/search_icon.png';
import _tabAdd from '@/assets/mobile-tab-bar/add_icon.png';
import _tabList from '@/assets/mobile-tab-bar/list_icon.png';

const tabHome = _tabHome as unknown as string;
const tabSearch = _tabSearch as unknown as string;
const tabAdd = _tabAdd as unknown as string;
const tabList = _tabList as unknown as string;
import avatarFallback from '@/assets/avatar.svg';
import ImportInspoModal from '@/components/blocks/ImportInspoModal';
import CreateListModal from '@/components/CreateListModal/CreateListModal';
import CreateItineraryModal from '@/components/CreateItineraryModal/CreateItineraryModal';
import CreateNewLinkFlow from '@/components/LinkPerformance/CreateNewLinkFlow';
import RecommendFromPostsFlowModal from '@/components/RecommendFromPostsFlowModal/RecommendFromPostsFlowModal';
import SignUpCTAPopover from '@/components/SignUpCTAPopover/SignUpCTAPopover';
import LoginModal from '@/components/LoginModal/LoginModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { useAuthAction } from '@/hooks/useAuthAction';
import { useTravelMenuActions } from '@/hooks/useTravelMenuActions';
import { WebAnalytics } from '@/lib/analytics';
import { getCityFreeformSearchTerm } from '@/utils/cityUtils';
import { isCreatorOrAdvisor } from '@/utils/isCreatorOrAdvisor';
import { hapticLight } from '@/lib/haptics';
import { isNativePlatform } from '@/lib/platform';
import {
    LOGGED_OUT_MOBILE_EXPLORE_CLEARED_EVENT,
    LOGGED_OUT_MOBILE_EXPLORE_SESSION_KEY,
} from '@/constants/loggedOutHomeQuery';
import {
    EVENT_CREATE_LIST_BUTTON_CLICKED,
    EVENT_RECOMMEND_PLACE_BUTTON_CLICKED,
    EVENT_MOBILE_TAB_TAPPED,
    EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED,
} from '@/lib/analyticsEvents';
import { useOpenTripImportRoute } from '@/hooks/useOpenTripImportRoute';
import { useOpenInspoImportRoute } from '@/hooks/useOpenInspoImportRoute';
import { useBackHandler } from '@/lib/backHandlerRegistry';
import { TabIconPerformance } from './MobileTabBarIcons';
import MobileCreateMenuSheet, { type MobileCreateMenuHandlers } from './MobileCreateMenuSheet';
import './MobileTabBar.css';

const MobileTabBar: React.FC = () => {
    const pathname = useLocation().pathname;
    const router = useNavigate();
    const { user } = useAuth();
    const {
        requireAuth,
        showPopover,
        getPopoverProps,
        showLoginModal,
        setShowLoginModal,
        loginModalRedirectPath,
        handleSignUpClick,
    } = useAuthAction();

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuClosing, setMenuClosing] = useState(false);
    const [showRecommendFromPostsFlow, setShowRecommendFromPostsFlow] = useState(false);
    const [showCreateListModal, setShowCreateListModal] = useState(false);
    const [createListOpenAddMode, setCreateListOpenAddMode] = useState<'copy-paste' | 'search' | false>(false);
    const [showCreateItineraryModal, setShowCreateItineraryModal] = useState(false);
    const [createItineraryOpenAddMode, setCreateItineraryOpenAddMode] = useState<boolean | 'social' | 'copy-paste' | 'search'>(false);
    const [showImportInspoModal, setShowImportInspoModal] = useState(false);
    const [showCreateLinkFlow, setShowCreateLinkFlow] = useState(false);
    const [signUpPopoverPath, setSignUpPopoverPath] = useState<string | undefined>(undefined);
    const [isShrunk, setIsShrunk] = useState(false);

    // Request the create-menu to close with animation. Sets `menuClosing`
    // which triggers the reverse keyframe; the sheet's onAnimationEnd finishes
    // the unmount by clearing both flags. Instant-unmount is only used when a
    // menu-item is tapped (we hand off to a modal / route change anyway).
    const requestCloseMenu = () => {
        if (!menuOpen || menuClosing) return;
        setMenuClosing(true);
    };
    const forceCloseMenu = () => {
        setMenuClosing(false);
        setMenuOpen(false);
    };
    const closeMenus = forceCloseMenu;
    const {
        handleMyTripsClick,
        handlePerformanceClick,
    } = useTravelMenuActions({ onBeforeNavigate: closeMenus });

    useBackHandler(() => requestCloseMenu(), menuOpen && !menuClosing);
    const isCreator = isCreatorOrAdvisor(user);
    const openInspoImportRoute = useOpenInspoImportRoute();
    const openTripImportRoute = useOpenTripImportRoute();
    const isHomeActive = pathname === '/';
    const isSearchActive = pathname === '/results';
    const isPerformanceActive =
        pathname === '/performance' || pathname.startsWith('/performance/');
    const isContentActive =
        pathname === '/my-travel' || pathname.startsWith('/my-travel/');
    const isFourthTabActive = isCreator ? isPerformanceActive : isContentActive;
    const isProfileActive = pathname === '/profile' || pathname.startsWith('/profile/');

    useEffect(() => {
        if (!showPopover) {
            setSignUpPopoverPath(undefined);
        }
    }, [showPopover]);

    // Shrink the tab bar while the user scrolls down any page's own scroll container,
    // restore full size on scroll up. Scroll events don't bubble, so listen on window
    // during the capture phase — same technique as ExploreHeader's scroll-aware backing.
    useEffect(() => {
        let lastScrollTop = 0;

        const handleScroll = (event: Event) => {
            const target = event.target;
            const scrollTop =
                target === document ? window.scrollY : (target as HTMLElement).scrollTop ?? 0;
            const delta = scrollTop - lastScrollTop;

            if (scrollTop <= 4) {
                setIsShrunk(false);
            } else if (delta > 4) {
                setIsShrunk(true);
            } else if (delta < -4) {
                setIsShrunk(false);
            }

            lastScrollTop = scrollTop;
        };

        window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        return () => window.removeEventListener('scroll', handleScroll, { capture: true });
    }, [pathname]);

    const handleHomeClick = () => {
        hapticLight();
        setIsShrunk(false);
        WebAnalytics.track(EVENT_MOBILE_TAB_TAPPED, { tab: 'home' });
        if (!user && typeof window !== 'undefined') {
            try {
                sessionStorage.removeItem(LOGGED_OUT_MOBILE_EXPLORE_SESSION_KEY);
                window.dispatchEvent(new CustomEvent(LOGGED_OUT_MOBILE_EXPLORE_CLEARED_EVENT));
            } catch {
                /* ignore */
            }
        }
        setMenuOpen(false);
        router('/');
    };

    const handleSearchClick = () => {
        hapticLight();
        setIsShrunk(false);
        WebAnalytics.track(EVENT_MOBILE_TAB_TAPPED, { tab: 'search' });

        const cityName = getCityFreeformSearchTerm(user?.city) ?? '';
        const resultsHref = cityName
            ? `/results?search=${encodeURIComponent(cityName)}`
            : '/results';

        if (!user) {
            setSignUpPopoverPath(resultsHref);
        }

        requireAuth('search', () => {
            setMenuOpen(false);
            router(resultsHref);
        });
    };

    const handlePlusToggle = () => {
        hapticLight();
        setIsShrunk(false);
        if (menuOpen) {
            requestCloseMenu();
            return;
        }
        setMenuClosing(false);
        setMenuOpen(true);
    };

    const handleFourthTabClick = () => {
        hapticLight();
        setIsShrunk(false);
        if (isCreator) {
            WebAnalytics.track(EVENT_MOBILE_TAB_TAPPED, { tab: 'performance' });
            if (!user) {
                requireAuth('profile', () => {});
                return;
            }
            setMenuOpen(false);
            handlePerformanceClick();
            return;
        }

        WebAnalytics.track(EVENT_MOBILE_TAB_TAPPED, { tab: 'content' });
        if (!user) {
            requireAuth('itineraries', () => {});
            return;
        }
        setMenuOpen(false);
        handleMyTripsClick();
    };

    const handleProfileClick = () => {
        hapticLight();
        setIsShrunk(false);
        WebAnalytics.track(EVENT_MOBILE_TAB_TAPPED, { tab: 'profile' });
        if (!user) {
            requireAuth('profile', () => {});
            return;
        }
        setMenuOpen(false);
        router(user?._id ? hrefForUserProfile({ _id: user._id, username: user.username, profile_type: user.profile_type }) : '/profile', { state: { fromTabBar: true } });
    };

    const handleRecommendPlace = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            WebAnalytics.trackContentCreation(EVENT_RECOMMEND_PLACE_BUTTON_CLICKED, {
                source_page: pathname || 'unknown',
                flow: 'recommend-place-mobile-tab-bar',
            });
            WebAnalytics.track(EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED, { item: 'recommend_place' });
            setShowRecommendFromPostsFlow(true);
        });
    };

    const handleCreateList = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            WebAnalytics.trackContentCreation(EVENT_CREATE_LIST_BUTTON_CLICKED, {
                source_page: pathname || 'unknown',
                flow: 'create-list-mobile-tab-bar',
            });
            WebAnalytics.track(EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED, { item: 'create_list' });
            setCreateListOpenAddMode('search');
            setShowCreateListModal(true);
        });
    };

    const handleCreateTrip = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            WebAnalytics.track(EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED, { item: 'create_trip' });
            setCreateItineraryOpenAddMode('search');
            setShowCreateItineraryModal(true);
        });
    };

    const handleImportTrip = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            WebAnalytics.track(EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED, { item: 'import_trip' });
            openInspoImportRoute();
        });
    };

    const handleTripImport = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            WebAnalytics.track(EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED, { item: 'itinerary_import' });
            openTripImportRoute({ entry: 'mobile_create_sheet' });
        });
    };

    const handleTripImportFromInspo = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            openTripImportRoute({ entry: 'start_building' });
        });
    };

    const handleCreateNewLink = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            WebAnalytics.track(EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED, { item: 'create_new_link' });
            setShowCreateLinkFlow(true);
        });
    };

    const handleMyLinks = () => {
        setMenuOpen(false);
        requireAuth('post', () => {
            WebAnalytics.track(EVENT_MOBILE_PLUS_MENU_ITEM_CLICKED, { item: 'my_links' });
            router('/performance/link');
        });
    };

    const menuHandlers: MobileCreateMenuHandlers = {
        onCreateNewLink: handleCreateNewLink,
        onMyLinks: handleMyLinks,
        onImportSocial: handleImportTrip,
        onRecommend: handleRecommendPlace,
        onPasteText: handleTripImport,
        onCreateList: handleCreateList,
        onCreateTrip: handleCreateTrip,
    };

    const handleSheetAnimationEnd = () => {
        if (menuClosing) {
            forceCloseMenu();
        }
    };

    const closeCreateListModal = () => {
        setShowCreateListModal(false);
        setCreateListOpenAddMode(false);
    };

    const closeCreateItineraryModal = () => {
        setShowCreateItineraryModal(false);
        setCreateItineraryOpenAddMode(false);
    };

    const handleCreateListFromInspo = () => {
        setShowImportInspoModal(false);
        setCreateListOpenAddMode('search');
        setShowCreateListModal(true);
    };

    const handleCreateTripFromInspo = () => {
        setShowImportInspoModal(false);
        setCreateItineraryOpenAddMode('search');
        setShowCreateItineraryModal(true);
    };

    const handleSocialImportFromInspo = () => {
        setCreateItineraryOpenAddMode('social');
        setShowCreateItineraryModal(true);
    };

    const profileImgSrc = (typeof user?.small_avatar_url === 'string' ? user.small_avatar_url : null) || avatarFallback;
    const isNative = isNativePlatform();

    return (
        <>
            <nav className={`mobile-tab-bar${isNative ? '' : ' mobile-tab-bar--web'}${isNative && isShrunk ? ' mobile-tab-bar--shrunk' : ''}`} aria-label="Mobile navigation">
                <div className={`mobile-tab-bar-inner${isNative ? ' mobile-tab-bar-inner--frosted' : ' mobile-tab-bar-inner--web'}`}>
                    <button
                        className={`mobile-tab-bar-btn ${isHomeActive ? 'active' : ''}`}
                        onClick={handleHomeClick}
                        aria-label="Home"
                    >
                        <img
                            src={tabHome}
                            alt=""
                            className="mobile-tab-bar-icon"
                            width={24}
                            height={24}
                        />
                    </button>

                    <button
                        className={`mobile-tab-bar-btn ${isSearchActive ? 'active' : ''}`}
                        onClick={handleSearchClick}
                        aria-label="Search"
                    >
                        <img
                            src={tabSearch}
                            alt=""
                            className="mobile-tab-bar-icon"
                            width={24}
                            height={24}
                        />
                    </button>

                    <button
                        className={`mobile-tab-bar-plus-btn ${menuOpen && !menuClosing ? 'open' : ''}`}
                        onClick={handlePlusToggle}
                        aria-label={menuOpen && !menuClosing ? 'Close menu' : 'Open create menu'}
                        aria-expanded={menuOpen && !menuClosing}
                    >
                        <img
                            src={tabAdd}
                            alt=""
                            className="mobile-tab-bar-icon mobile-tab-bar-icon--plus"
                            width={32}
                            height={32}
                        />
                    </button>

                    <button
                        className={`mobile-tab-bar-btn ${isFourthTabActive ? 'active' : ''}`}
                        aria-label={isCreator ? 'Performance' : 'My Trips'}
                        type="button"
                        onClick={handleFourthTabClick}
                    >
                        {isCreator ? (
                            <TabIconPerformance className="mobile-tab-bar-icon mobile-tab-bar-icon--svg" />
                        ) : (
                            <img
                                src={tabList}
                                alt=""
                                className="mobile-tab-bar-icon"
                                width={24}
                                height={24}
                            />
                        )}
                    </button>

                    <button
                        className={`mobile-tab-bar-btn ${isProfileActive ? 'active' : ''}`}
                        onClick={handleProfileClick}
                        aria-label="Profile"
                    >
                        <img
                            src={profileImgSrc}
                            alt=""
                            className="mobile-tab-bar-avatar"
                            width={32}
                            height={32}
                        />
                    </button>
                </div>
            </nav>

            {menuOpen && typeof document !== 'undefined' && ReactDOM.createPortal(
                <>
                    <div
                        className={`mobile-tab-menu-overlay${menuClosing ? ' mobile-tab-menu-overlay--closing' : ''}`}
                        onClick={requestCloseMenu}
                    />
                    <MobileCreateMenuSheet
                        variant={isCreator ? 'creator' : 'traveller'}
                        closing={menuClosing}
                        onAnimationEnd={handleSheetAnimationEnd}
                        handlers={menuHandlers}
                    />
                </>,
                document.body
            )}

            {showRecommendFromPostsFlow && typeof document !== 'undefined' && ReactDOM.createPortal(
                <RecommendFromPostsFlowModal
                    isOpen
                    onClose={() => setShowRecommendFromPostsFlow(false)}
                />,
                document.body
            )}

            {showCreateListModal && typeof document !== 'undefined' && ReactDOM.createPortal(
                <CreateListModal
                    onClose={closeCreateListModal}
                    onListCreated={closeCreateListModal}
                    openAddModeOnCreate={createListOpenAddMode}
                />,
                document.body
            )}

            {showCreateItineraryModal && typeof document !== 'undefined' && ReactDOM.createPortal(
                <CreateItineraryModal
                    onClose={closeCreateItineraryModal}
                    onTripCreated={closeCreateItineraryModal}
                    openAddModeOnCreate={createItineraryOpenAddMode}
                />,
                document.body
            )}

            {showImportInspoModal && typeof document !== 'undefined' && ReactDOM.createPortal(
                <ImportInspoModal
                    isOpen={showImportInspoModal}
                    onClose={() => setShowImportInspoModal(false)}
                    onCreateListClick={handleCreateListFromInspo}
                    onCreateTripClick={handleCreateTripFromInspo}
                    onSocialImportClick={handleSocialImportFromInspo}
                    onTripImportClick={handleTripImportFromInspo}
                />,
                document.body
            )}

            {showCreateLinkFlow && typeof document !== 'undefined' && ReactDOM.createPortal(
                <CreateNewLinkFlow
                    isOpen={showCreateLinkFlow}
                    onClose={() => setShowCreateLinkFlow(false)}
                />,
                document.body
            )}

            {showPopover && (() => {
                const popoverProps = getPopoverProps();
                if (!popoverProps) return null;
                return (
                    <SignUpCTAPopover
                        title={popoverProps.title}
                        description={popoverProps.description}
                        onClose={popoverProps.onClose}
                        redirectPath={signUpPopoverPath}
                        onSignUpClick={() => handleSignUpClick(signUpPopoverPath)}
                    />
                );
            })()}

            {showLoginModal && (
                <LoginModal
                    onClose={() => setShowLoginModal(false)}
                    redirectPath={loginModalRedirectPath}
                    initialMode="signup"
                />
            )}
        </>
    );
};

export default MobileTabBar;
